import gql, { disableFragmentWarnings } from 'graphql-tag'

disableFragmentWarnings()

const FRAGMENT_USER = `
  fragment userFields on user {
    id
    firstName: firstname
    lastName: lastname
    avatarUrl: avatar_url
  }
`

const FRAGMENT_FILE = `
  fragment fileFields on content_asset {
    asset {
      id
      url
      description
      type
    }
  }
`

const FRAGMENT_MESSAGE = `
  ${FRAGMENT_USER}
  ${FRAGMENT_FILE}
  fragment messageFields on message {
    id
    createdAt: created_at
    status
    senderUser: user {
      ...userFields
    }
    content {
      body
      files: content_assets {
        ...fileFields
      }
    }
  }
`

const FRAGMENT_ROOM = `
  fragment roomFields on group {
    id
    name
    type
    status
    updatedAt: updated_at
  }
`

const FRAGMENT_ROOM_USERS = `
  fragment roomUserFields on user {
    ...userFields
    roles: user_roles {
      role {
        id
        name
      }
    }
    unreadMessages: user_unread_messages {
      roomId: group_id
      count: unread_message_count
    }
    lastSeenAt: updated_at
  }
`

export const QUERY_MESSAGE = gql`
  ${FRAGMENT_MESSAGE}
  query Messages($roomId: Int, $startAfterId: Int, $limit: Int) {
    messages: message(where: {id: {_lt: $startAfterId}, message_groups: {group_id: {_eq: $roomId}}}, limit: $limit, order_by: {created_at: desc}) {
      ...messageFields
      replyMessage: message {
        ...messageFields
      }
    }
    message_aggregate(where: {message_groups: {group_id: {_eq: $roomId}}}) {
      aggregate {
        count
      }
    }
  }
`

export const MUTATION_NEW_MESSAGE = gql`
  ${FRAGMENT_MESSAGE}
  mutation NewMessage($userId: Int!, $roomId: Int!, $messageObject: message_insert_input!, $timestamptz: timestamptz) {
    newMessage: insert_message_one(object: $messageObject) {
      ...messageFields
      replyMessage: message {
        ...messageFields
      }
    }
    update_group_by_pk(pk_columns: {id: $roomId}, _set: {updated_at: $timestamptz}) {
      id
    }
    update_user_group(where: {user_id: {_eq: $userId}, group_id: {_eq: $roomId}}, _set: {updated_at: $timestamptz}) {
      affected_rows
    }
  }
`

export const SUBSCRIPTION_NEW_MESSAGE = gql`
  ${FRAGMENT_MESSAGE}
  subscription NewMessage($roomId: Int!, $messageId: Int!) {
    newMessage: message(where: {id: {_gt: $messageId}, message_groups: {group_id: {_eq: $roomId}}}) {
      ...messageFields
      replyMessage: message {
        ...messageFields
      }
    }
  }
`

export const MUTATION_DELETE_MESSAGE = gql`
  ${FRAGMENT_MESSAGE}
  mutation NewMessage($messageId: Int!) {
    deletedMessage: update_message_by_pk(pk_columns: {id: $messageId}, _set: {status: DELETED}) {
      ...messageFields
      replyMessage: message {
        ...messageFields
      }
    }
  }
`

export const SUBSCRIBE_DELETED_MESSAGE = gql`
  ${FRAGMENT_MESSAGE}
  subscription DeletedMessage($roomId: Int!) {
    deletedMessages: message(where: {status: {_eq: DELETED}, message_groups: {group_id: {_eq: $roomId}}}) {
      ...messageFields
      replyMessage: message {
        ...messageFields
      }
    }
  }
`

export const MUTATION_MARK_MESSAGES_READ = gql`
  mutation MarkMessagesRead($userId: Int!, $roomId: Int!, $timestamptz: timestamptz) {
    update_user_group(where: {user_id: {_eq: $userId}, group_id: {_eq: $roomId}}, _set: {updated_at: $timestamptz}) {
      affected_rows
    }
  }
`

export const QUERY_ROOMS = gql`
  ${FRAGMENT_ROOM}
  ${FRAGMENT_MESSAGE}
  ${FRAGMENT_ROOM_USERS}
  query Rooms($userId: Int!, $startAfterTimestamp: timestamptz, $limit: Int, $offset: Int, $search: String!) {
    rooms: group(
      where: {
        _and: [
          {
            _and: [
              { updated_at: { _lt: $startAfterTimestamp } }
              { status: { _neq: DELETED } }
              { type: { _in: [DIRECT_MESSAGE, GROUP_MESSAGE] } }
              { user_groups: { user_id: { _eq: $userId }  } }
            ]
          }
          {
            user_groups: {
              user: {
                _or: [
                  { firstname: { _ilike: $search } }
                  { lastname: { _ilike: $search } }
                ]
              }
            }
          }
        ]
      }
      order_by: { updated_at: desc }
      limit: $limit
      offset: $offset
    ) {
      ...roomFields
      lastMessage: message_groups(where: {message: {type: {_nin: [NOTIFICATION]}}}, order_by: {message: {created_at: desc}}, limit: 1) {
        message {
          ...messageFields
        }
      }
      users: user_groups {
        user {
          ...roomUserFields
        }
      }
    }
    group_aggregate(
      where: {
        _and: [
          {
            _and: [
              { updated_at: { _lt: $startAfterTimestamp } }
              { status: { _neq: DELETED } }
              { type: { _in: [DIRECT_MESSAGE, GROUP_MESSAGE] } }
              { user_groups: { user_id: { _eq: $userId }  } }
            ]
          }
          {
            user_groups: {
              user: {
                _or: [
                  { firstname: { _ilike: $search } }
                  { lastname: { _ilike: $search } }
                ]
              }
            }
          }
        ]
      }
      order_by: { updated_at: desc }
    ) {
      aggregate {
        count
      }
    }
  }
`

export const QUERY_EXISTING_ROOMS = gql`
  ${FRAGMENT_ROOM}
  ${FRAGMENT_MESSAGE}
  ${FRAGMENT_ROOM_USERS}
  query existingRooms($userId: Int!) {
    existingRooms: group(where: {status: {_neq: DELETED}, type: {_in: [DIRECT_MESSAGE, GROUP_MESSAGE]}, user_groups: {user_id: {_eq: $userId}}}) {
      ...roomFields
        lastMessage: message_groups(where: {message: {type: {_nin: [NOTIFICATION]}}}, order_by: {message: {created_at: desc}}, limit: 1) {
          message {
            ...messageFields
          }
        }
        users: user_groups {
          user {
            ...roomUserFields
          }
        }
      }
    }
  `

export const MUTATION_NEW_ROOM = gql`
  ${FRAGMENT_ROOM}
  ${FRAGMENT_MESSAGE}
  ${FRAGMENT_ROOM_USERS}
  mutation NewRoom($userId: Int!, $roomName: String!, $roomType: enum_group_type_enum!, $userData: [user_group_insert_input!]!) {
    newRoom: insert_group_one(object: {message_groups: {data: {message: {data: {status: PUBLISHED, type: SYSTEM_MESSAGE, sender_user_id: $userId, content: {data: {body: "You are now connected!"}}}}}}, name: $roomName, type: $roomType, user_groups: {data: $userData}}) {
      ...roomFields
      lastMessage: message_groups(where: {message: {type: {_nin: [NOTIFICATION]}}}, order_by: {message: {created_at: desc}}, limit: 1) {
        message {
          ...messageFields
        }
      }
      users: user_groups {
        user {
          ...roomUserFields
        }
      }
    }
  }
`

export const SUBSCRIPTION_NEW_ROOM = gql`
  ${FRAGMENT_ROOM}
  ${FRAGMENT_MESSAGE}
  ${FRAGMENT_ROOM_USERS}
  subscription NewRooms($userId: Int!, $startAfterTimestamp: timestamptz) {
    rooms: group(where: {updated_at: {_gt: $startAfterTimestamp}, status: {_neq: DELETED}, type: {_in: [DIRECT_MESSAGE, GROUP_MESSAGE]}, user_groups: {user_id: {_eq: $userId}}}, order_by: {updated_at: desc}) {
      ...roomFields
      lastMessage: message_groups(where: {message: {type: {_nin: [NOTIFICATION]}}}, order_by: {message: {created_at: desc}}, limit: 1) {
        message {
          ...messageFields
        }
      }
      users: user_groups {
        user {
          ...roomUserFields
        }
      }
    }
  }
`

export const MUTATION_UPDATE_ROOM_NAME = gql`
  ${FRAGMENT_ROOM}
  ${FRAGMENT_MESSAGE}
  ${FRAGMENT_ROOM_USERS}
  mutation UpdateRoomName($roomId: Int!, $newRoomName: String!) {
    updateRoom: update_group(where: {id: {_eq: $roomId}}, _set: {name: $newRoomName}) {
      affected_rows
      returning {
        ...roomFields
        lastMessage: message_groups(where: {message: {type: {_nin: [NOTIFICATION]}}}, order_by: {message: {created_at: desc}}, limit: 1) {
          message {
            ...messageFields
          }
        }
        users: user_groups {
          user {
            ...roomUserFields
          }
        }
      }
    }
  }
`

export const MUTATION_DELETE_ROOM = gql`
  mutation DeleteRoom($roomId: Int!) {
    update_group(where: {id: {_eq: $roomId}}, _set: {status: DELETED}) {
      affected_rows
    }
  }
`

export const SUBSCRIPTION_DELETED_ROOMS = gql`
  ${FRAGMENT_ROOM}
  ${FRAGMENT_MESSAGE}
  ${FRAGMENT_ROOM_USERS}
  subscription DeletedRooms($userId: Int!) {
    deletedRooms: group(where: {status: {_eq: DELETED}, type: {_in: [DIRECT_MESSAGE, GROUP_MESSAGE]}, user_groups: {user_id: {_eq: $userId}}}) {
      ...roomFields
      lastMessage: message_groups(where: {message: {type: {_nin: [NOTIFICATION]}}}, order_by: {message: {created_at: desc}}, limit: 1) {
        message {
          ...messageFields
        }
      }
      users: user_groups {
        user {
          ...roomUserFields
        }
      }
    }
  }
`

export const SUBSCRIPTION_UNREAD_MESSAGE_COUNT = gql`
  subscription UnreadMessageCount($userId: Int!) {
    unreadMessages: user_unread_message(where: {user_id: {_eq: $userId}}) {
      roomId: group_id
      userId: user_id
      lastSeen: last_seen
      count: unread_message_count
    }
  }
`

export const QUERY_CONTACTS = gql`
  ${FRAGMENT_USER}
  query Contacts($search: String, $userId: Int!, $roleIds: [Int!]!, $limit: Int) {
    contacts: search_user(args: {search: $search}, where: {id: {_neq: $userId}, _or: [{user_roles: {role_id: {_in: $roleIds}}, user_groups: {group: {user_groups: {user_id: {_eq: $userId}}}}}, {user_roles: {role_id: {_in: $roleIds}}}]}, limit: $limit) {
      ...userFields
      roles: user_roles(where: {role_id: {_in: $roleIds}}) {
        role {
          id
          name
        }
      }
      lastSeenAt: updated_at
    }
  }
`
